import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CreditCard, Save, Loader2, CheckCircle, XCircle,
  ExternalLink, ArrowLeft, AlertTriangle, Key, Eye, EyeOff,
  Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const StripeIntegrationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  
  const [status, setStatus] = useState({
    configured: false,
    stripe_account_id: null,
    connected_at: null
  });
  
  const [credentials, setCredentials] = useState({
    publishable_key: '',
    secret_key: ''
  });
  
  const [showSecretKey, setShowSecretKey] = useState(false);

  // Feature toggles (Feb-7-2026) — parity with the MamoPay integration
  // page. Without these, every payment-link request defaulted to
  // Stripe even when the merchant wanted MamoPay for driver COD links.
  const [features, setFeatures] = useState({
    enable_for_leads: true,
    enable_for_orders: true,
    enable_for_drivers: true,
    send_customer_receipt: true,
    primary_gateway: 'stripe',
  });
  const [savingFeatures, setSavingFeatures] = useState(false);

  const fetchFeatures = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_BASE_URL}/settings/stripe-features`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFeatures({
        enable_for_leads:      r.data.enable_for_leads !== false,
        enable_for_orders:     r.data.enable_for_orders !== false,
        enable_for_drivers:    r.data.enable_for_drivers !== false,
        send_customer_receipt: r.data.send_customer_receipt !== false,
        primary_gateway:       r.data.primary_gateway || 'stripe',
      });
      setWebhookSecretSaved(!!r.data.webhook_secret_set);
    } catch (e) { /* non-fatal */ }
  }, []);

  const saveFeatures = async () => {
    setSavingFeatures(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/settings/stripe-features`,
        { ...features, set_as_primary: features.primary_gateway === 'stripe' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Stripe feature settings saved');
    } catch (e) {
      toast.error('Failed to save: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingFeatures(false);
    }
  };

  // Webhook secret (Feb-7-2026) — durability fix. The merchant
  // configures a webhook in Stripe Dashboard pointing at
  // /api/payment/stripe/webhook with event checkout.session.completed
  // and pastes the `whsec_...` signing secret here. Stored on
  // companies.integrations.stripe.webhook_secret so our handler can
  // verify incoming Stripe signatures even when the customer closes
  // their tab before /payment-success fires verify-session.
  const [webhookSecret, setWebhookSecret] = useState('');
  const [webhookSecretSaved, setWebhookSecretSaved] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [savingWebhookSecret, setSavingWebhookSecret] = useState(false);

  const saveWebhookSecret = async () => {
    const value = (webhookSecret || '').trim();
    if (!value.startsWith('whsec_')) {
      toast.error('Stripe signing secrets start with `whsec_`. Double-check the value in Stripe Dashboard → Developers → Webhooks.');
      return;
    }
    setSavingWebhookSecret(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/settings/stripe-webhook-secret`,
        { webhook_secret: value },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setWebhookSecretSaved(true);
      setWebhookSecret('');
      toast.success('Webhook secret saved. Server-to-server reconciliation is now active.');
    } catch (e) {
      toast.error('Failed to save: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingWebhookSecret(false);
    }
  };

  const setPrimary = async (gw) => {
    setFeatures((p) => ({ ...p, primary_gateway: gw }));
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/settings/payment-link-gateway?gateway=${gw}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${gw === 'stripe' ? 'Stripe' : 'MamoPay'} is now the primary gateway`);
    } catch (e) {
      toast.error('Failed to update primary gateway');
    }
  };
  
  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/tracking/stripe/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch Stripe status:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchStatus();
    fetchFeatures();
  }, [fetchStatus, fetchFeatures]);
  
  const handleConnect = async () => {
    if (!credentials.publishable_key || !credentials.secret_key) {
      toast.error('Please enter both Publishable Key and Secret Key');
      return;
    }
    
    setConnecting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('stripe_publishable_key', credentials.publishable_key);
      formData.append('stripe_secret_key', credentials.secret_key);
      
      const response = await axios.post(`${API_BASE_URL}/tracking/stripe/connect`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Stripe connected successfully');
      setStatus({
        configured: true,
        stripe_account_id: response.data.account_id,
        connected_at: new Date().toISOString()
      });
      setCredentials({ publishable_key: '', secret_key: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Stripe');
    } finally {
      setConnecting(false);
    }
  };
  
  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/tracking/stripe/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Stripe disconnected');
      setStatus({ configured: false, stripe_account_id: null, connected_at: null });
      setShowDisconnectDialog(false);
    } catch (error) {
      toast.error('Failed to disconnect Stripe');
    } finally {
      setDisconnecting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-wt-gold" />
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/integrations')}
          className="text-wt-text-muted hover:text-wt-text-primary"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">Stripe Integration</h1>
          <p className="text-wt-text-muted text-sm">Accept payments for tips and transactions</p>
        </div>
      </div>
      
      {/* Status Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-wt-surface border border-wt-border rounded-xl p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl">
              <CreditCard className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-wt-text-primary">Stripe</h2>
              <p className="text-sm text-wt-text-muted">Payment processing platform</p>
            </div>
          </div>
          <Badge 
            className={status.configured 
              ? 'bg-green-500/20 text-green-400 border-green-500/30' 
              : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
            }
          >
            {status.configured ? (
              <><CheckCircle className="w-3 h-3 mr-1" /> Connected</>
            ) : (
              <><XCircle className="w-3 h-3 mr-1" /> Not Connected</>
            )}
          </Badge>
        </div>
        
        {status.configured ? (
          <div className="space-y-4">
            <div className="bg-wt-bg rounded-lg p-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-sm text-wt-text-muted">Account ID</p>
                  <p className="text-wt-text-primary font-mono">{status.stripe_account_id}</p>
                </div>
                <div>
                  <p className="text-sm text-wt-text-muted">Connected At</p>
                  <p className="text-wt-text-primary">
                    {status.connected_at ? new Date(status.connected_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => window.open('https://dashboard.stripe.com', '_blank')}
                className="border-wt-border text-wt-text-primary"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Stripe Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowDisconnectDialog(true)}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Disconnect
              </Button>
            </div>

            {/* Feature toggles + primary-gateway picker. Parity with
                the MamoPay integration page. */}
            <div className="bg-wt-bg rounded-lg p-5 space-y-5 border border-wt-border mt-2">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-wt-text-primary font-semibold">Feature Toggles</h3>
                  <p className="text-xs text-wt-text-muted mt-1">
                    Control where Stripe payment links can be generated.
                  </p>
                </div>
                <Button
                  onClick={saveFeatures}
                  disabled={savingFeatures}
                  className="bg-emerald-500 hover:bg-emerald-500/90 text-white"
                  data-testid="save-stripe-features-button"
                >
                  {savingFeatures
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                    : <><Save className="w-4 h-4 mr-2" />Save</>
                  }
                </Button>
              </div>

              {[
                { key: 'enable_for_leads',      label: 'Enable for Leads',                desc: 'Allow Stripe payment links from lead/CRM screens.' },
                { key: 'enable_for_orders',     label: 'Enable for Orders',               desc: 'Allow Stripe payment links from the order detail panel (admin-initiated).' },
                { key: 'enable_for_drivers',    label: 'Enable for Drivers',              desc: 'Allow drivers to create Stripe links from the driver mobile app.' },
                { key: 'send_customer_receipt', label: 'Send Stripe Receipt to Customer', desc: 'Stripe emails the customer a receipt with the payment confirmation.' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-start justify-between gap-4 pt-3 border-t border-wt-border first:border-t-0 first:pt-0">
                  <div className="flex-1 min-w-0">
                    <Label className="text-wt-text-primary text-sm" htmlFor={`stripe-${key}`}>{label}</Label>
                    <p className="text-xs text-wt-text-muted mt-0.5">{desc}</p>
                  </div>
                  <Switch
                    id={`stripe-${key}`}
                    checked={!!features[key]}
                    onCheckedChange={(v) => setFeatures((p) => ({ ...p, [key]: v }))}
                    data-testid={`stripe-toggle-${key}`}
                  />
                </div>
              ))}

              <div className="pt-3 border-t border-wt-border">
                <Label className="text-wt-text-primary text-sm">Primary Payment Gateway</Label>
                <p className="text-xs text-wt-text-muted mt-0.5 mb-3">
                  When the same feature is enabled in both Stripe and MamoPay, the primary gateway is used.
                </p>
                <div className="flex gap-2">
                  <Button
                    variant={features.primary_gateway === 'stripe' ? 'default' : 'outline'}
                    onClick={() => setPrimary('stripe')}
                    className={features.primary_gateway === 'stripe'
                      ? 'bg-indigo-500 hover:bg-indigo-500/90 text-white'
                      : 'border-wt-border text-wt-text-primary'}
                    data-testid="set-primary-stripe-button"
                  >
                    {features.primary_gateway === 'stripe' && <CheckCircle className="w-4 h-4 mr-2" />}
                    Stripe is Primary
                  </Button>
                  <Button
                    variant={features.primary_gateway === 'mamopay' ? 'default' : 'outline'}
                    onClick={() => setPrimary('mamopay')}
                    className={features.primary_gateway === 'mamopay'
                      ? 'bg-purple-500 hover:bg-purple-500/90 text-white'
                      : 'border-wt-border text-wt-text-primary'}
                    data-testid="set-primary-mamopay-button"
                  >
                    {features.primary_gateway === 'mamopay' && <CheckCircle className="w-4 h-4 mr-2" />}
                    MamoPay is Primary
                  </Button>
                </div>
              </div>
            </div>

            {/* Webhook Secret — required for server-to-server payment
                reconciliation. Without this, only the success-URL
                verify-session path runs (customer must reach
                /payment-success in their browser). With it, Stripe
                pings us directly when checkout.session.completed
                fires — durable even when the customer closes their
                tab mid-redirect. */}
            <div className="bg-wt-bg rounded-lg p-5 space-y-3 border border-wt-border mt-2">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-wt-text-primary font-semibold flex items-center gap-2">
                    Webhook Signing Secret
                    {webhookSecretSaved && (
                      <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px]">
                        <CheckCircle className="w-3 h-3 mr-1" /> Configured
                      </Badge>
                    )}
                  </h3>
                  <p className="text-xs text-wt-text-muted mt-1 max-w-xl">
                    Optional but recommended. Lets Stripe notify us the instant a customer pays — even if they close their tab.
                  </p>
                </div>
              </div>
              <div className="bg-wt-surface rounded-lg p-3 text-xs text-wt-text-secondary border border-wt-border">
                <p className="font-semibold text-wt-text-primary mb-1">How to get it:</p>
                <ol className="list-decimal pl-4 space-y-0.5">
                  <li>Open <a className="text-indigo-300 underline" target="_blank" rel="noreferrer" href="https://dashboard.stripe.com/webhooks">Stripe Dashboard → Developers → Webhooks</a>.</li>
                  <li>Click <strong>Add endpoint</strong>.</li>
                  <li>Endpoint URL: <code className="bg-wt-bg px-1.5 py-0.5 rounded">{`${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/payment/stripe/webhook`}</code></li>
                  <li>Event: <code>checkout.session.completed</code></li>
                  <li>Click <strong>Reveal signing secret</strong> and paste the <code>whsec_...</code> value below.</li>
                </ol>
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showWebhookSecret ? 'text' : 'password'}
                    placeholder={webhookSecretSaved ? 'Paste a new whsec_… to replace the saved one' : 'whsec_…'}
                    value={webhookSecret}
                    onChange={(e) => setWebhookSecret(e.target.value)}
                    className="bg-wt-surface border-wt-border text-wt-text-primary pr-10"
                    data-testid="stripe-webhook-secret-input"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    onClick={() => setShowWebhookSecret((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-wt-text-muted hover:text-wt-text-primary"
                    aria-label="toggle visibility"
                  >
                    {showWebhookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  onClick={saveWebhookSecret}
                  disabled={savingWebhookSecret || !webhookSecret.trim()}
                  className="bg-indigo-500 hover:bg-indigo-500/90 text-white"
                  data-testid="save-stripe-webhook-secret-button"
                >
                  {savingWebhookSecret
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                    : <><Key className="w-4 h-4 mr-2" />Save Secret</>
                  }
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-400 font-medium">Setup Required</p>
                  <p className="text-sm text-wt-text-muted mt-1">
                    To accept tips and payments, you need to connect your Stripe account. 
                    Get your API keys from the{' '}
                    <a 
                      href="https://dashboard.stripe.com/apikeys" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-amber-400 underline hover:no-underline"
                    >
                      Stripe Dashboard
                    </a>.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-wt-text-primary flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Publishable Key
                </Label>
                <Input
                  value={credentials.publishable_key}
                  onChange={(e) => setCredentials(prev => ({ ...prev, publishable_key: e.target.value }))}
                  placeholder="pk_live_..."
                  className="bg-wt-bg border-wt-border text-wt-text-primary font-mono"
                />
                <p className="text-xs text-wt-text-muted">Starts with pk_live_ or pk_test_</p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-wt-text-primary flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Secret Key
                </Label>
                <div className="relative">
                  <Input
                    type={showSecretKey ? 'text' : 'password'}
                    value={credentials.secret_key}
                    onChange={(e) => setCredentials(prev => ({ ...prev, secret_key: e.target.value }))}
                    placeholder="sk_live_..."
                    className="bg-wt-bg border-wt-border text-wt-text-primary font-mono pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecretKey(!showSecretKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-wt-text-muted hover:text-wt-text-primary"
                  >
                    {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-wt-text-muted">Starts with sk_live_ or sk_test_ - Keep this secure!</p>
              </div>
              
              <Button
                onClick={handleConnect}
                disabled={connecting || !credentials.publishable_key || !credentials.secret_key}
                className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white"
              >
                {connecting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Connect Stripe
              </Button>
            </div>
          </div>
        )}
      </motion.div>
      
      {/* Features Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-wt-surface border border-wt-border rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-wt-text-primary mb-4">What you can do with Stripe</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <CreditCard className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <p className="font-medium text-wt-text-primary">Customer Tips</p>
              <p className="text-sm text-wt-text-muted">Let customers tip drivers on the tracking page</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <CreditCard className="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <p className="font-medium text-wt-text-primary">Payment Links</p>
              <p className="text-sm text-wt-text-muted">Create payment links for orders</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <CreditCard className="w-4 h-4 text-purple-400" />
            </div>
            <div>
              <p className="font-medium text-wt-text-primary">Secure Processing</p>
              <p className="text-sm text-wt-text-muted">PCI-compliant payment handling</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <CreditCard className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <p className="font-medium text-wt-text-primary">Multi-currency</p>
              <p className="text-sm text-wt-text-muted">Accept payments in multiple currencies</p>
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Disconnect Dialog */}
      <Dialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <DialogContent className="bg-wt-surface border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Disconnect Stripe?</DialogTitle>
            <DialogDescription className="text-wt-text-muted">
              This will disable tip payments and any other Stripe-powered features. 
              You can reconnect at any time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDisconnectDialog(false)}
              className="border-wt-border text-wt-text-primary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {disconnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StripeIntegrationPage;
